/**
 * usePushNotifications — small hook that:
 *   1. Registers /service-worker.js on first call (idempotent)
 *   2. Exposes `subscribe()` to ask the user for permission and POST
 *      the resulting PushSubscription to /api/push/subscribe
 *   3. Exposes `unsubscribe()` to revoke the subscription on the
 *      browser AND tell the backend to drop the row.
 *
 * The hook never *automatically* prompts — the user must click the
 * "Enable push notifications" button in their notification settings.
 *
 * If the browser doesn't support PushManager / Notification, or if the
 * server hasn't been configured with a VAPID public key (returns 503),
 * `supported` is false and the UI hides the toggle.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = window.__BACKEND_URL__;

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
};

export default function usePushNotifications() {
  const [supported, setSupported] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'default');
  const [subscribed, setSubscribed] = useState(false);
  const [pending, setPending] = useState(false);

  useEffect(() => {
    const isSupported = (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
    setSupported(isSupported);
    if (!isSupported) return;

    let alive = true;
    (async () => {
      try {
        const reg = await navigator.serviceWorker.ready;
        const existing = await reg.pushManager.getSubscription();
        if (alive) setSubscribed(!!existing);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, []);

  const subscribe = useCallback(async () => {
    if (!supported) return { ok: false, reason: 'unsupported' };
    setPending(true);
    try {
      // Permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') return { ok: false, reason: 'permission_denied' };

      // VAPID key
      let publicKey;
      try {
        const { data } = await axios.get(`${API_URL}/api/push/vapid-public-key`, { withCredentials: true });
        publicKey = data.public_key;
      } catch (err) {
        // 503 → server has no key configured. Surface honestly.
        if (err?.response?.status === 503) return { ok: false, reason: 'server_not_configured' };
        throw err;
      }
      if (!publicKey) return { ok: false, reason: 'server_not_configured' };

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });

      await axios.post(`${API_URL}/api/push/subscribe`, sub.toJSON(), { withCredentials: true });
      setSubscribed(true);
      return { ok: true };
    } catch (err) {
      return { ok: false, reason: err?.message || 'unknown_error' };
    } finally {
      setPending(false);
    }
  }, [supported]);

  const unsubscribe = useCallback(async () => {
    if (!supported) return { ok: false };
    setPending(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        try { await axios.post(`${API_URL}/api/push/unsubscribe`, { endpoint: sub.endpoint }, { withCredentials: true }); }
        catch { /* non-fatal */ }
        await sub.unsubscribe();
      }
      setSubscribed(false);
      return { ok: true };
    } catch (err) {
      return { ok: false, reason: err?.message || 'unknown_error' };
    } finally {
      setPending(false);
    }
  }, [supported]);

  return { supported, permission, subscribed, pending, subscribe, unsubscribe };
}
